import React from "react"
import * as Yup from 'yup'
import Ajax from "../../../Ajax"
import { useFormik } from "formik"
import { observer } from "mobx-react-lite"
import Field from "../../form/fields/Field"
import { Alert, AlertModal } from "../../.."
import { store } from "../../../stores/Store"
import ViewQuestionnaire from "./ViewQuestionnaire"
import { Button } from "@mui/material"
import { SelectOption } from "../../form/fields/SelectField"
import { Questionnaire } from "../../../models/Interfaces"

interface CreateQuestionnaireFormProps {
    questionnaire?: Questionnaire;
    answersJson?: string;
    preloadedAnswersJson?: string;
}

export default observer(function CreateQuestionnaireForm ({questionnaire = null, answersJson = null, preloadedAnswersJson = null}: CreateQuestionnaireFormProps) {

    const { templates, getTemplates, getQuestionnaires } = store.QuestionnaireStore
    const userAccount = store.NodeStore.accounts.find((a) => a.uid === store.AppStore.sessionInfo.userAccount.uid)
    const showAdviserUidField = !store.AppStore.checkJwtRoles(["client_account", "sponsor_account"], false)

    React.useEffect(() => {
        if (!templates?.length) {
            getTemplates()
        }
    }, [])

    const formik = useFormik({

        initialValues: {
            advisorUid: questionnaire ? questionnaire.respondent.advisorUid : userAccount.advisorUid,
            displayName: questionnaire?.displayName,
            templateUid: questionnaire?.template.uid,
            respondentEmail: questionnaire?.respondent.respondentEmail,
            notificationEmail: store.AppStore.sessionInfo.userEmail,
            answers: answersJson,
            preloadedAnswers: preloadedAnswersJson
        },

        validationSchema: Yup.object({
            advisorUid: Yup.string().required("This field is required"),
            displayName: Yup.string().matches(/^[ ,.@!$%#';:?()[\]~_+=À-Ö\p{Sc}\p{So}\p{Mn}\p{P}\p{Z}À-ÿ\w\n]*$/u, "You have used some illegal characters").required("This field is required"),
            templateUid: Yup.string().required("This field is required"),
            respondentEmail: Yup.string().matches(
                /\b[\w\.-]+@[\w\.-]+\.\w{2,4}\b/,
                "A valid email is required"
            ).required("This field is required"),
        }),

        onSubmit: async (values, actions) => {
            
            values.respondentEmail = values.respondentEmail.toLowerCase()
            
            await Ajax.Questionnaire.Create(values).then((response) => {
                getQuestionnaires()
                store.AlertStore.Close()
                AlertModal({width: "700px", body: <ViewQuestionnaire uid={response.data?.uid} />})
            }).catch((response) => {
                Alert({message: response.data?.detail, color: "error"})
            })
        }
    })

    var templateOptions = Array.isArray(templates) && templates.length ? (
        [...templates].sort((a, b) => a.displayName.localeCompare(b.displayName)).filter(t => t.active).map((template) => {
            return {value: template.uid, label: template.displayName, help: template.version}
        }) 
    ) : []

    var clientOptions: SelectOption[] = []
    // Add customer accounts
    store.NodeStore.accounts.filter((a) => !!a.advisorUid).forEach((a) => {
        clientOptions.push({value: a.advisorUid, label: a.label})
    })

    return (
        <form className="form" onSubmit={formik.handleSubmit}>
            <div className="form-header">
                <div>
                    <h2>Create Questionnaire</h2>
                    <p>Complete this form to create a new questionnaire for your client.</p>
                    <p>Once the questionnaire is created, you will be provided with a URL which you can send to the intended respondent.</p>
                    {answersJson || preloadedAnswersJson ? <p>You are copying answers from another questionnaire.</p> : null}
                </div>
            </div>

            <div className="form-fields" style={{maxWidth: "270px"}}>
                <Field.Text
                    formik
                    name="displayName"
                    label="Reference/label"
                    helpText="This can be the name of the applicant or job. The applicant won't see this."
                    onChange={formik.handleChange}
                    value={formik.values.displayName}
                    warning={formik.submitCount && formik.errors.displayName ? formik.errors.displayName : false} />

                <Field.Text
                    formik
                    name="respondentEmail"
                    label="Respondent contact email"
                    helpText="This is for your own record, we won't contact or share this email with anyone."
                    onChange={formik.handleChange}
                    value={formik.values.respondentEmail}
                    warning={formik.submitCount && formik.errors.respondentEmail ? formik.errors.respondentEmail : false} />
                
                <Field.Select
                    formik
                    label="Select a Questionnaire type"
                    name="templateUid"
                    disabled={!templates}
                    onChange={formik.handleChange}
                    value={formik.values.templateUid}
                    warning={formik.submitCount && formik.errors.templateUid ? formik.errors.templateUid : ""}
                    options={templateOptions} />

                {showAdviserUidField ? (
                    <Field.Select
                        formik
                        label="Assign to a Sponsor (optional)"
                        helpText="Enables filtering and allows authorised sponsor users to view this questionnaire."
                        name="advisorUid"
                        onChange={formik.handleChange}
                        value={formik.values.advisorUid}
                        warning={formik.submitCount && formik.errors.advisorUid ? formik.errors.advisorUid : false}
                        options={clientOptions} />
                ) : null}
            </div>

            <div className="form-buttons">
                <Button className="btn btn-sm" onClick={() => store.AlertStore.Close()}>Cancel</Button>
                <Button className="btn btn-sm" color="success" type="submit" variant="contained" disabled={formik.isSubmitting}>Create</Button>
            </div>
        </form>
    )
})